/**
 * Book presentation: how each sportsbook looks, keyed by its `bookmakers.bookmaker_id`.
 * The catalog (name, kind, order) lives in the database; this file holds only what a database can't:
 * badges, colors, logo assets and link hosts. A book without an entry renders via `presentationFor`'s
 * fallback, and CI (`scripts/check-book-presentation.ts`) fails when a catalog book has no entry here.
 * Declaration order is the landing strip's order.
 */
import type { BookId } from '@sharpr/types';

export interface BookBadge {
  bg: string;
  text: string;
}

export interface BookPresentation {
  /** 2-3 letter badge text, unique across books */
  abbreviation: string;
  /** Hex color for charts */
  hexColor: string;
  /** Tailwind classes for the badge shown when no logo loads */
  badge: BookBadge;
  logo?: string;
  darkLogo?: string;
  thumbnail?: string;
  /** Tailwind transform class for thumbnails with unusual padding or weight */
  thumbnailScale?: string;
  /** Tailwind transform class for logos with unusual padding or weight */
  logoScale?: string;
  /** Present when the book shows on the landing strip */
  landing?: { src: string; heightClass?: string };
  /** Host suffixes a shared book link may point at */
  linkHosts?: readonly string[];
}

export const DEFAULT_BOOK_HEX_COLOR = '#64748b';

const FALLBACK_BADGE: BookBadge = { bg: 'bg-slate-600', text: 'text-white' };

export const BOOK_PRESENTATION: Readonly<Record<BookId, BookPresentation>> = {
  draftkings: {
    abbreviation: 'DK', hexColor: '#16a34a',
    badge: { bg: 'bg-orange-500', text: 'text-white' },
    logo: '/logos/sportsbooks/draftkings.png',
    darkLogo: '/logos/sportsbooks-dark/draftkings.svg',
    thumbnail: '/logos/thumbnails/draftkings.webp',
    logoScale: 'scale-125',
    landing: { src: '/logos/sportsbooks-dark/draftkings.svg' },
    linkHosts: ['draftkings.com'],
  },
  fanduel: {
    abbreviation: 'FD', hexColor: '#2563eb',
    badge: { bg: 'bg-blue-600', text: 'text-white' },
    logo: '/logos/sportsbooks/fanduel.png',
    darkLogo: '/logos/sportsbooks-dark/fanduel.webp',
    thumbnail: '/logos/thumbnails/fanduel.png',
    thumbnailScale: 'scale-150',
    logoScale: 'scale-125',
    landing: { src: '/logos/sportsbooks-dark/fanduel.svg' },
    linkHosts: ['fanduel.com'],
  },
  betmgm: {
    abbreviation: 'MGM', hexColor: '#c4a052',
    badge: { bg: 'bg-yellow-500', text: 'text-black' },
    logo: '/logos/sportsbooks/betmgm.png',
    darkLogo: '/logos/sportsbooks-dark/betmgm.png',
    thumbnail: '/logos/thumbnails/betmgm.jpg',
    landing: { src: '/logos/sportsbooks-dark/betmgm.svg' },
    linkHosts: ['betmgm.com'],
  },
  caesars: {
    abbreviation: 'CS', hexColor: '#1e3a5f',
    badge: { bg: 'bg-gradient-to-br from-yellow-400 to-red-600', text: 'text-white' },
    logo: '/logos/sportsbooks/caesars.png',
    darkLogo: '/logos/sportsbooks-dark/caesars.png',
    thumbnail: '/logos/thumbnails/caesars.png',
    landing: { src: '/logos/sportsbooks-dark/caesars.svg' },
    linkHosts: ['caesars.com', 'williamhill.com'],
  },
  betonlineag: {
    abbreviation: 'BOL', hexColor: '#b91c1c',
    badge: { bg: 'bg-red-700', text: 'text-white' },
    logo: '/logos/sportsbooks/betonline.png',
    darkLogo: '/logos/sportsbooks/betonline.png',
    thumbnail: '/logos/thumbnails/betonline.webp',
  },
  bovada: {
    abbreviation: 'BOV', hexColor: '#cc0000',
    badge: { bg: 'bg-red-600', text: 'text-white' },
    logo: '/logos/sportsbooks/bovada.svg',
    darkLogo: '/logos/sportsbooks-dark/bovada.svg',
    thumbnail: '/logos/thumbnails/bovada.webp',
  },
  fanatics: {
    abbreviation: 'FAN', hexColor: '#0066cc',
    badge: { bg: 'bg-black', text: 'text-white' },
    logo: '/logos/sportsbooks/fanatics.png',
    darkLogo: '/logos/sportsbooks-dark/fanatics.png',
    thumbnail: '/logos/thumbnails/fanatics.png',
    thumbnailScale: 'scale-[1.85]',
    logoScale: 'scale-125',
    landing: { src: '/logos/sportsbooks-dark/fanatics.svg', heightClass: 'h-9 md:h-10' },
    linkHosts: ['fanatics.com'],
  },
  betrivers: {
    abbreviation: 'BR', hexColor: '#1e88e5',
    badge: { bg: 'bg-blue-700', text: 'text-white' },
    logo: '/logos/sportsbooks/betrivers.png',
    darkLogo: '/logos/sportsbooks-dark/betrivers.svg',
    thumbnail: '/logos/thumbnails/betrivers.png',
    landing: { src: '/logos/sportsbooks-dark/betrivers.svg' },
    linkHosts: ['betrivers.com'],
  },
  thescore: {
    abbreviation: 'tSB', hexColor: '#d00',
    badge: { bg: 'bg-red-600', text: 'text-white' },
    logo: '/logos/sportsbooks/theScore.png',
    darkLogo: '/logos/sportsbooks-dark/theScore.png',
    thumbnail: '/logos/thumbnails/theScore.png',
    linkHosts: ['espnbet.com', 'thescore.bet'],
  },
  hardrockbet: {
    abbreviation: 'HRB', hexColor: '#9333ea',
    badge: { bg: 'bg-black', text: 'text-white' },
    logo: '/logos/sportsbooks/hardrockbet.svg',
    darkLogo: '/logos/sportsbooks-dark/hardrockbet.svg',
    thumbnail: '/logos/thumbnails/hardrockbet.png',
    landing: { src: '/logos/sportsbooks-dark/hardrockbet.svg', heightClass: 'h-9 md:h-10' },
    linkHosts: ['hardrock.bet', 'hardrockbet.com'],
  },
  fliff: {
    abbreviation: 'FF', hexColor: '#6366f1',
    badge: { bg: 'bg-purple-600', text: 'text-white' },
    logo: '/logos/sportsbooks/fliff.png',
    darkLogo: '/logos/sportsbooks-dark/fliff.png',
    thumbnail: '/logos/thumbnails/fliff.png',
    thumbnailScale: 'scale-50',
    landing: { src: '/logos/sportsbooks-dark/fliff.svg' },
  },
  ballybet: {
    abbreviation: 'BB', hexColor: '#dc2626',
    badge: { bg: 'bg-red-600', text: 'text-white' },
    logo: '/logos/sportsbooks/ballybet.png',
    darkLogo: '/logos/sportsbooks-dark/ballybet.png',
    thumbnail: '/logos/thumbnails/ballybet.png',
  },
  betparx: {
    abbreviation: 'BPX', hexColor: '#f97316',
    badge: { bg: 'bg-orange-500', text: 'text-white' },
    logo: '/logos/sportsbooks/betparx.png',
    darkLogo: '/logos/sportsbooks-dark/betparx.svg',
    thumbnail: '/logos/thumbnails/betparx.png',
    linkHosts: ['betparx.com'],
  },
  rebet: {
    abbreviation: 'RB', hexColor: '#a855f7',
    badge: { bg: 'bg-purple-500', text: 'text-white' },
    logo: '/logos/sportsbooks/rebet.svg',
    darkLogo: '/logos/sportsbooks-dark/rebet.svg',
    thumbnail: '/logos/thumbnails/rebet.png',
  },
  betr: {
    abbreviation: 'BTR', hexColor: '#a21caf',
    badge: { bg: 'bg-fuchsia-700', text: 'text-white' },
    logo: '/logos/sportsbooks/betr.svg',
    darkLogo: '/logos/sportsbooks-dark/betr.svg',
    thumbnail: '/logos/thumbnails/betr.png',
  },
  underdog: {
    abbreviation: 'UD', hexColor: '#fbbf24',
    badge: { bg: 'bg-yellow-400', text: 'text-black' },
    logo: '/logos/sportsbooks/underdog.png',
    darkLogo: '/logos/sportsbooks-dark/underdog.png',
    thumbnail: '/logos/thumbnails/underdog.svg',
    linkHosts: ['underdogfantasy.com'],
  },
  'pick6': {
    abbreviation: 'P6', hexColor: '#ea580c',
    badge: { bg: 'bg-orange-500', text: 'text-white' },
    logo: '/logos/sportsbooks/pick6.png',
    darkLogo: '/logos/sportsbooks-dark/pick6.webp',
    thumbnail: '/logos/thumbnails/pick6.png',
  },
  prizepicks: {
    abbreviation: 'PP', hexColor: '#8b5cf6',
    badge: { bg: 'bg-green-500', text: 'text-white' },
    logo: '/logos/sportsbooks/prizepicks.svg',
    darkLogo: '/logos/sportsbooks-dark/prizepicks.svg',
    thumbnail: '/logos/thumbnails/prizepicks.avif',
    logoScale: 'scale-125',
    linkHosts: ['prizepicks.com'],
  },
  betr_us_dfs: {
    abbreviation: 'BTP', hexColor: '#16a34a',
    badge: { bg: 'bg-green-600', text: 'text-white' },
    logo: '/logos/sportsbooks/betr.svg',
    darkLogo: '/logos/sportsbooks-dark/betr.svg',
    thumbnail: '/logos/thumbnails/betr.png',
  },
  sleeper: {
    abbreviation: 'SLP', hexColor: '#1f6feb',
    badge: { bg: 'bg-slate-800', text: 'text-white' },
    logo: '/logos/sportsbooks/sleeper.png',
    darkLogo: '/logos/sportsbooks-dark/sleeper.png',
    thumbnail: '/logos/thumbnails/sleeper.png',
    linkHosts: ['sleeper.com'],
  },
  parlayplay: {
    abbreviation: 'PLP', hexColor: '#7c3aed',
    badge: { bg: 'bg-violet-600', text: 'text-white' },
    logo: '/logos/sportsbooks/parlayplay.svg',
    darkLogo: '/logos/sportsbooks-dark/parlayplay.svg',
    thumbnail: '/logos/thumbnails/parlayplay.png',
  },
  chalkboard: {
    abbreviation: 'CB', hexColor: '#0ea5e9',
    badge: { bg: 'bg-sky-500', text: 'text-white' },
    logo: '/logos/sportsbooks/chalkboard.png',
    darkLogo: '/logos/sportsbooks-dark/chalkboard.svg',
    thumbnail: '/logos/thumbnails/chalkboard.png',
  },
  dabble: {
    abbreviation: 'DAB', hexColor: '#7c3aed',
    badge: { bg: 'bg-violet-600', text: 'text-white' },
    logo: '/logos/sportsbooks/dabble.png',
    darkLogo: '/logos/sportsbooks-dark/dabble.png',
    thumbnail: '/logos/thumbnails/dabble.png',
  },
  prophetx: {
    abbreviation: 'PRX', hexColor: '#1a2f5c',
    badge: { bg: 'bg-slate-800', text: 'text-white' },
    logo: '/logos/sportsbooks/prophetx.svg',
    darkLogo: '/logos/sportsbooks-dark/prophetx.svg',
    thumbnail: '/logos/thumbnails/prophetx.png',
    landing: { src: '/logos/sportsbooks-dark/prophetx.svg' },
  },
  novig: {
    abbreviation: 'NV', hexColor: '#10b981',
    badge: { bg: 'bg-emerald-500', text: 'text-white' },
    logo: '/logos/sportsbooks/novig.svg',
    darkLogo: '/logos/sportsbooks-dark/novig.svg',
    thumbnail: '/logos/thumbnails/novig.png',
    landing: { src: '/logos/sportsbooks-dark/novig.svg' },
    linkHosts: ['novig.us'],
  },
  polymarket: {
    abbreviation: 'PM', hexColor: '#2563eb',
    badge: { bg: 'bg-blue-600', text: 'text-white' },
    logo: '/logos/sportsbooks/polymarket.png',
    darkLogo: '/logos/sportsbooks-dark/polymarket.webp',
    thumbnail: '/logos/thumbnails/polymarket.png',
    landing: { src: '/logos/sportsbooks-dark/polymarket.svg' },
    linkHosts: ['polymarket.com'],
  },
  kalshi: {
    abbreviation: 'KS', hexColor: '#21c891',
    badge: { bg: 'bg-emerald-500', text: 'text-white' },
    logo: '/logos/sportsbooks/kalshi.svg',
    darkLogo: '/logos/sportsbooks-dark/kalshi.svg',
    logoScale: 'scale-75',
    thumbnail: '/logos/thumbnails/kalshi.png',
    landing: { src: '/logos/sportsbooks-dark/kalshi.svg' },
  },
  betopenly: {
    abbreviation: 'OPN', hexColor: '#ed1c24',
    badge: { bg: 'bg-red-600', text: 'text-white' },
    logo: '/logos/sportsbooks/betopenly.svg',
    darkLogo: '/logos/sportsbooks-dark/betopenly.svg',
    thumbnail: '/logos/thumbnails/betopenly.png',
  },
  pinnacle: {
    abbreviation: 'PIN', hexColor: '#ff5f00',
    badge: { bg: 'bg-orange-600', text: 'text-white' },
    logo: '/logos/sportsbooks/pinnacle.svg',
    darkLogo: '/logos/sportsbooks-dark/pinnacle.svg',
    thumbnail: '/logos/thumbnails/pinnacle.png',
  },
  'bet365': {
    abbreviation: '365', hexColor: '#027b5b',
    badge: { bg: 'bg-emerald-700', text: 'text-white' },
    logo: '/logos/sportsbooks/bet365.svg',
    logoScale: 'scale-90',
    thumbnail: '/logos/thumbnails/bet365.png',
    linkHosts: ['bet365.com'],
  },
  circa: {
    abbreviation: 'CIR', hexColor: '#a6192e',
    badge: { bg: 'bg-red-800', text: 'text-white' },
    logo: '/logos/sportsbooks/circa.png',
    darkLogo: '/logos/sportsbooks-dark/circa.png',
    thumbnail: '/logos/thumbnails/circa.png',
  },
  courtside: {
    abbreviation: 'CTS', hexColor: '#f04e0c',
    badge: { bg: 'bg-orange-600', text: 'text-white' },
    logo: '/logos/sportsbooks/courtside.svg',
    darkLogo: '/logos/sportsbooks-dark/courtside.svg',
    thumbnail: '/logos/thumbnails/courtside.png',
  },
  onyx: {
    abbreviation: 'ONX', hexColor: '#b23cfd',
    badge: { bg: 'bg-purple-500', text: 'text-white' },
    logo: '/logos/sportsbooks/onyx.svg',
    darkLogo: '/logos/sportsbooks-dark/onyx.svg',
    thumbnail: '/logos/thumbnails/onyx.png',
  },
  propsbuilder: {
    abbreviation: 'PB', hexColor: '#2f6fd6',
    badge: { bg: 'bg-blue-600', text: 'text-white' },
    logo: '/logos/sportsbooks/propsbuilder.png',
    darkLogo: '/logos/sportsbooks-dark/propsbuilder.png',
    thumbnail: '/logos/thumbnails/propsbuilder.png',
  },
  bet105: {
    abbreviation: '105', hexColor: '#3083dc',
    badge: { bg: 'bg-blue-600', text: 'text-white' },
    logo: '/logos/sportsbooks/bet105.svg',
    darkLogo: '/logos/sportsbooks-dark/bet105.svg',
    thumbnail: '/logos/thumbnails/bet105.png',
  },
  splash: {
    abbreviation: 'SPL', hexColor: '#4bebe2',
    badge: { bg: 'bg-cyan-500', text: 'text-white' },
    logo: '/logos/sportsbooks/splash.svg',
    darkLogo: '/logos/sportsbooks-dark/splash.svg',
    logoScale: 'scale-110',
    thumbnail: '/logos/thumbnails/splash.png',
  },
};

/** Multi-word names give initials (up to 3, upper-case); one word gives its first two letters; empty gives '?'. */
export function fallbackAbbreviation(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return words
    .slice(0, 3)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
}

/** A book's presentation: its entry, or a neutral badge built from its name. */
export function presentationFor(id: BookId, name: string): BookPresentation {
  return (
    (Object.hasOwn(BOOK_PRESENTATION, id) ? BOOK_PRESENTATION[id] : undefined) ?? {
      abbreviation: fallbackAbbreviation(name),
      hexColor: DEFAULT_BOOK_HEX_COLOR,
      badge: { ...FALLBACK_BADGE },
    }
  );
}

/** The ids with no presentation entry. */
export function missingPresentation(ids: readonly string[]): string[] {
  return ids.filter((id) => !Object.hasOwn(BOOK_PRESENTATION, id));
}
